import { useTheme } from '@mui/material'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useMessagesDashboardViewContext } from 'pages/Messages/MessagesDashboardViewContext'
import { useEffect, useState } from 'react'
import { Box, Tooltip, Fade } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { MESSAGE_STATUS_COLORS } from 'common/constants'

export interface MessageVolumeSparklineProps {
  onToggleChart?: () => void
  isChartVisible?: boolean
  isFirstTime?: boolean
}

export default function MessageVolumeSparkline(props: MessageVolumeSparklineProps) {
  const theme = useTheme()
  const messageViewContext = useMessagesDashboardViewContext()
  const [series, setSeries] = useState<any>()
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    if (!messageViewContext.traffic) {
      return
    }

    const series = new Map<string, Array<Array<any>>>()
    const contextTraffic = messageViewContext.traffic

    // Calculate time range to determine appropriate data grouping
    const buckets = contextTraffic.traffic.buckets
    if (buckets.length > 0) {
      const firstTime = new Date(buckets[0].datetime).getTime()
      const lastTime = new Date(buckets[buckets.length - 1].datetime).getTime()
      const timeRange = lastTime - firstTime
      const daysRange = timeRange / (1000 * 60 * 60 * 24)

      // Determine grouping interval based on time range
      let groupInterval = 1 // default 1 hour
      if (daysRange > 30) {
        groupInterval = 24 // group by day for ranges > 30 days
      } else if (daysRange > 7) {
        groupInterval = 6 // group by 6 hours for ranges > 7 days
      } else if (daysRange > 1) {
        groupInterval = 2 // group by 2 hours for ranges > 1 day
      }

      // Group data points to reduce noise
      const groupedData = new Map<string, { sum: number; count: number }>()

      buckets.forEach(bucket => {
        const dateTime = new Date(bucket.datetime).getTime()
        const groupKey =
          Math.floor(dateTime / (groupInterval * 60 * 60 * 1000)) * (groupInterval * 60 * 60 * 1000)

        if (!groupedData.has(groupKey.toString())) {
          groupedData.set(groupKey.toString(), { sum: bucket.count, count: 1 })
        } else {
          const existing = groupedData.get(groupKey.toString())!
          existing.sum += bucket.count
          existing.count += 1
        }
      })

      // Convert grouped data to series format
      const sortedKeys = Array.from(groupedData.keys()).sort((a, b) => parseInt(a) - parseInt(b))
      const averagedData = sortedKeys.map(key => {
        const data = groupedData.get(key)!
        return [parseInt(key), data.sum / data.count] // Use average for smoother line
      })

      series.set('total', averagedData)
    }

    const chartSeries: any = []
    series.forEach((value, key) => {
      chartSeries.push({
        name: key,
        data: value,
        color: props.isChartVisible ? theme.palette.primary.main : MESSAGE_STATUS_COLORS.processed,
      })
    })

    setSeries(chartSeries)
  }, [messageViewContext.traffic, props.isChartVisible, theme.palette.primary.main])

  const options = {
    chart: {
      type: 'spline',
      height: 30,
      width: 300,
      margin: [2, 0, 2, 0],
      backgroundColor: null,
      borderWidth: 0,
      shadow: false,
      style: {
        overflow: 'visible',
      },
      skipClone: true,
    },
    title: {
      text: '',
    },
    xAxis: {
      type: 'datetime', // Use datetime for xAxis
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      startOnTick: false,
      endOnTick: false,
      tickPositions: [],
      lineWidth: 0, // Hide xAxis line
    },
    yAxis: {
      endOnTick: false,
      startOnTick: false,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      tickPositions: [0],
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      enabled: false, // Disable Highcharts tooltip to avoid conflicts
    },
    plotOptions: {
      series: {
        animation: true, // Enable animation for smoother transitions
        lineWidth: props.isChartVisible ? 3 : 2, // Thicker line when chart is visible
        shadow: false,
        states: {
          hover: {
            lineWidth: props.isChartVisible ? 4 : 3,
          },
        },
        marker: {
          enabled: false, // Hide markers to reduce visual noise
        },
        fillOpacity: 0.25,
        turboThreshold: 5000, // Increase threshold to handle large data
        dataGrouping: {
          enabled: true, // Enable data grouping
          approximation: 'average', // Use average for smoother lines
          groupPixelWidth: 3, // Group pixels for better performance
          forced: true, // Force grouping even for small datasets
        },
        // Add smoothing
        smooth: true,
        // Reduce noise with moving average
        pointStart: 0,
        pointInterval: 1,
      },
    },
    credits: {
      enabled: false,
    },
    series,
  }

  const handleClick = () => {
    if (props.onToggleChart) {
      props.onToggleChart()
    }
  }

  const tooltipContent = props.isChartVisible
    ? 'Click to hide detailed traffic chart'
    : 'Click to show detailed traffic chart'

  return (
    <>
      <Tooltip
        title={tooltipContent}
        placement="top"
        arrow
        PopperProps={{
          sx: {
            zIndex: 9999, // Ensure tooltip appears above other elements
          },
        }}
      >
        <Box
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            cursor: 'pointer',
            position: 'relative',
            display: 'inline-block',
            transition: 'opacity 0.2s ease-in-out',
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          <HighchartsReact highcharts={Highcharts} options={options} />

          {/* Hover Icon */}
          <Fade in={isHovered} timeout={200}>
            <Box
              sx={{
                position: 'absolute',
                top: -2,
                left: -2,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              {props.isChartVisible ? (
                <VisibilityOff sx={{ fontSize: 12, color: 'white' }} />
              ) : (
                <Visibility sx={{ fontSize: 12, color: 'white' }} />
              )}
            </Box>
          </Fade>

          {/* Active State Badge */}
          {props.isChartVisible && (
            <Box
              sx={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 8,
                height: 8,
                backgroundColor: theme.palette.primary.main,
                borderRadius: '50%',
                border: `2px solid ${theme.palette.background.paper}`,
              }}
            />
          )}
        </Box>
      </Tooltip>
    </>
  )
}
